import { getDateLocale as getInterfaceDateLocale } from '@/utils/i18n';
import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import { Eye, MoreVertical, Pencil, BarChart3, Download, Code, Trash2, FolderInput } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useState, useEffect } from 'react';
import { DeleteConfirmModal } from './DeleteConfirmModal';
import { CopyCodeModal } from '@/components/shared/CopyCodeModal';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';

interface VideoModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  video: {
    id: string;
    title: string;
    thumbnailUrl: string;
    viewCount: number;
    createdAt?: string;
  } | null;
}

export function VideoModal({ open, onOpenChange, video }: VideoModalProps) {
  useLanguage();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showEmbedModal, setShowEmbedModal] = useState(false);
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canEdit = hasPermission('videos', 'edit');

  useEffect(() => {
    if (open && !canEdit) {
      onOpenChange(false);
    }
  }, [open, canEdit, onOpenChange]);

  const defaultFeatures = {
    autoPlay: false,
    showControls: true,
    loop: false,
    loopVideo: false,
  };

  if (!video) return null;

  const formattedDate = video.createdAt
    ? formatDistanceToNow(new Date(video.createdAt), {
        addSuffix: true,
        locale: getInterfaceDateLocale(),
      })
    : '';

  const handleAction = (action: string) => {
    switch (action) {
      case 'personalizar':
        navigate(`/personalizations/${video.id}`);
        onOpenChange(false);
        break;
      case 'metricas':
        navigate(`/metrics?videoId=${video.id}`);
        onOpenChange(false);
        break;
      case 'baixar':
        console.log('Download video:', video.id);
        break;
      case 'copy-code':
        setShowEmbedModal(true);
        break;
      case 'mover':
        console.log('Move to folder:', video.id);
        break;
      case 'deletar':
        setShowDeleteConfirm(true);
        break;
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-full w-[95vw] md:max-w-3xl lg:max-w-6xl dark:bg-[rgb(var(--modal-bg))] dark:border-[rgb(var(--modal-border))]">
          <DialogTitle className="sr-only">{video.title}</DialogTitle>
          
          {/* Video Container com Overlay */}
          <div className="relative aspect-video bg-gray-100 dark:bg-muted rounded-lg overflow-hidden">
            {/* Thumbnail */}
            <img
              src={video.thumbnailUrl}
              alt={video.title}
              className="w-full h-full object-cover"
            />
            
            {/* Overlay Gradient Bottom */}
            <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-black/80 via-black/40 to-transparent pointer-events-none" />
            
            {/* Título e Visualizações (bottom-left) */}
            <div className="absolute bottom-0 left-0 right-12 p-3 md:p-4 lg:p-5 text-white z-10">
              <h3 className="font-semibold text-base md:text-lg lg:text-xl mb-1 line-clamp-2">
                {video.title}
              </h3>
              <div className="flex items-center gap-1.5 text-xs md:text-sm text-white/90">
                <Eye className="h-3 w-3 md:h-4 md:w-4" />
                <span>{video.viewCount.toLocaleString()} {" "}{translateInterfaceText("visualizações")}</span>
                {formattedDate && (
                  <>
                    <span>·</span>
                    <span>{formattedDate}</span>
                  </>
                )}
              </div>
            </div>
            
            {/* Menu 3 Pontinhos (top-right) */}
            <div className="absolute top-2 right-2 md:top-3 md:right-3 z-10">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 md:h-9 md:w-9 lg:h-10 lg:w-10 bg-black/40 hover:bg-black/60 text-white border-0"
                    aria-label={translateInterfaceText("Mais opções")}
                  >
                    <MoreVertical className="h-5 w-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuItem onClick={() => handleAction('personalizar')}>
                    <Pencil className="h-4 w-4 mr-2" />
                    {translateInterfaceText("Personalizar")}</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleAction('metricas')}>
                    <BarChart3 className="h-4 w-4 mr-2" />
                    {translateInterfaceText("Métricas")}</DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => handleAction('copy-code')}>
                    <Code className="h-4 w-4 mr-2" />
                    {translateInterfaceText("Copiar código")}</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleAction('baixar')}>
                    <Download className="h-4 w-4 mr-2" />
                    {translateInterfaceText("Download")}</DropdownMenuItem>
                  {canEdit && (
                    <DropdownMenuItem onClick={() => handleAction('mover')}>
                      <FolderInput className="h-4 w-4 mr-2" />
                      {translateInterfaceText("Mover de pasta")}</DropdownMenuItem>
                  )}
                  {canEdit && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={() => handleAction('deletar')}
                        className="text-red-600 dark:text-red-400 data-[highlighted]:bg-red-600 data-[highlighted]:text-white"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        {translateInterfaceText("Excluir vídeo")}</DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <DeleteConfirmModal
        open={showDeleteConfirm}
        onOpenChange={setShowDeleteConfirm}
        videoId={video.id}
        videoTitle={video.title}
        onConfirm={() => {
          onOpenChange(false);
          setShowDeleteConfirm(false);
        }}
      />

      <CopyCodeModal
        isOpen={showEmbedModal}
        onClose={() => setShowEmbedModal(false)}
        entityType="video"
        entityId={video.id}
        entityName={video.title}
        jsCode={`<script src="https://cdn.hostvsl.com/player.js"></script>
<div id="hostvsl-player" data-video="${video.id}"></div>
<script>
  HostVSL.renderPlayer('#hostvsl-player', { videoId: '${video.id}' });
</script>`}
      />
    </>
  );
}
